import { useRef, useEffect } from "react";
import { Space, Button, Input, Checkbox } from "antd";
import { DatePicker } from "../../../components/antOverrides";
import moment from "moment";
import { Select } from "antd";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import {
  ClientsSelect,
  EmployeesSelect,
} from "../../../components/forms/AutoCompleteComponent";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { usePermission } from "../../../hooks/accessControl";

const { Option } = Select;

const Header = (props) => {
  const {
    activeDate,
    effective_to,
    onDateChange,
    onEndDateChange,
    activeGap,
    onGapChange,
    onStaffChange,
    onClientChange,
    onTextChange,
    onShowHiddenReasonsChange,
    showHiddenReasons,
    textFilter,
    reload,
    onSendSms,
    branchId,
    diariesApproval,
  } = props;
  const intl = useIntl();
  const inputRef = useRef();
  const hasManagedGapReasonsFeature = isAllowedFeature("managed_gap_reasons");

  const direction = useSelector(
    (state) => state.user.currentAppLocale.direction
  );
  useEffect(() => {
    if (inputRef && inputRef.current) {
      const { input } = inputRef.current;
      input.focus();
    }
  }, []);
  const gapslist = [
    {
      name: "lack_of_extraction_of_hours",
    },
    {
      name: "providing_overtime",
    },
    {
      name: "ok2go",
    },
    ...(branchId
      ? [
          {
            name: "overlaps",
          },
        ]
      : []),
  ];

  const staffFilter = { filter: { $and: [{ staff_member: true }] } };

  const hasSmsPermission = usePermission(branchId, "employees", "sms_sender");

  const disabledDate = (current) => {
    if (!activeDate) return false;

    // Get the start and end of the selected month
    const startOfMonth = moment(activeDate).startOf("month");
    const endOfMonth = moment(activeDate).endOf("month");

    // Disable the date if it's not within the active month
    return !current.isBetween(startOfMonth, endOfMonth, "day", "[]");
  };

  const handleDateChange = (date) => {
    // Call the original onDateChange handler
    onDateChange(date);

    if (date) {
      const newMonth = moment(date).month();
      const newYear = moment(date).year();
      const currentMonth = moment().month();
      const currentYear = moment().year();

      // Check if the selected month is the current month
      if (newMonth === currentMonth && newYear === currentYear) {
        // Set effective_to to yesterday (end of day)
        const yesterday = moment().subtract(1, "days").endOf("day");
        onEndDateChange(yesterday);
      }
      // If effective_to exists, check if it's within the new active month
      else if (effective_to) {
        const effectiveMonth = moment(effective_to).month();
        const effectiveYear = moment(effective_to).year();

        // If effective_to is not in the same month as the new activeDate, reset it
        if (newMonth !== effectiveMonth || newYear !== effectiveYear) {
          onEndDateChange(undefined);
        }
      }
    }
  };
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Space direction="horizontal">
        <DatePicker
          value={activeDate && moment(activeDate)}
          onChange={handleDateChange}
          picker="month"
          format={"MM/YYYY"}
          placeholder={intl.formatMessage({
            id: `app.gaps.select_month`,
          })}
          allowClear={false}
        />
        {!diariesApproval && (
          <Select
            style={{ width: 150 }}
            onChange={onGapChange}
            placeholder={intl.formatMessage({
              id: `app.gaps.select_gap`,
            })}
            value={activeGap}
          >
            {gapslist.map((option) => (
              <Option key={option.name} value={option.name}>
                {intl.formatMessage({
                  id: `app.gaps.${option.name}`,
                })}
              </Option>
            ))}
          </Select>
        )}
        {activeGap !== "overlaps" && (
          <>
            <Input
              prefix={
                <SearchOutlined style={{ padding: "0  5px", opacity: 0.4 }} />
              }
              placeholder={intl.formatMessage({
                id:
                  activeGap === "unapproved_minutes"
                    ? "app.gaps.search_by_name_or_id"
                    : `app.gaps.search_by_patient`,
              })}
              value={textFilter}
              onChange={onTextChange}
              style={{ width: 220 }}
              allowClear
              ref={inputRef}
            />
            <EmployeesSelect
              placeholder={intl.formatMessage({
                id: `app.gaps.search_by_staff`,
              })}
              style={{ width: 150 }}
              onChange={onStaffChange}
              allowClear
              extraParams={{
                ...staffFilter,
                query: { assigned_to_employee: true },
                scopePrefix: branchId ? `branches/${branchId}/` : ``,
              }}
            />
            <ClientsSelect
              placeholder={intl.formatMessage({
                id: `app.gaps.search_by_client`,
              })}
              style={{ width: 180 }}
              onChange={onClientChange}
              required={true}
              extraParams={{
                scopePrefix: branchId ? `branches/${branchId}/` : ``,
              }}
            />
          </>
        )}
        {activeGap === "ok2go" && (
          <DatePicker
            value={effective_to ? moment(effective_to) : undefined}
            onChange={onEndDateChange}
            picker="date"
            format={"DD/MM/YYYY"}
            placeholder={intl.formatMessage({
              id: `app.gaps.select_end_date`,
            })}
            allowClear={true}
            disabledDate={disabledDate}
            style={{ width: 150 }}
          />
        )}
        {hasManagedGapReasonsFeature &&
          activeGap === "lack_of_extraction_of_hours" && (
            <Checkbox
              style={{ margin: "0 20px" }}
              onChange={onShowHiddenReasonsChange}
              checked={showHiddenReasons}
            >
              {intl.formatMessage({
                id: `app.actions.include_hidden_reasons`,
              })}
            </Checkbox>
          )}
      </Space>

      <Space direction="horizontal">
        {diariesApproval &&
          branchId &&
          isAllowedFeature("send_sms") &&
          hasSmsPermission && (
            <Button type="primary" onClick={onSendSms} size="large">
              {intl.formatMessage({
                id: `app.actions.send_diaries_reminder`,
              })}
            </Button>
          )}

        <Button
          type="text"
          onClick={reload}
          size="large"
          shape="circle"
          icon={
            <ReloadOutlined
              className={direction === "rtl" ? "reloadRtl" : "reload"}
            />
          }
        />
      </Space>
    </div>
  );
};

export default Header;
